import {
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { I18nSelectPipe, KeyValuePipe, NgFor, NgIf } from '@angular/common';
import {
    AppointmentAnimationState,
    appointmentAnimations,
} from '../appointment-animations';
import { FeedbackComponent } from '../feedback/feedback.component';
import { ImprintComponent } from '../imprint/imprint.component';
import { AppointmentViewModel } from '../models/appointment';
import { AppointmentDatesViewModel } from '../models/date';
import { IUser } from '../models/user';
import { AppointmentService } from '../shared/appointment.service';
import { ModalComponent } from '../shared/modal/modal.component';
import { Translations } from '../shared/translations';
import { UserService } from '../shared/user.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['home.component.scss'],
    providers: [AppointmentService],
    imports: [I18nSelectPipe, ModalComponent, KeyValuePipe, ImprintComponent, FeedbackComponent, NgIf, NgFor],
    animations: appointmentAnimations,
})
export class HomeComponent implements OnInit, OnDestroy {
    private readonly ngUnsubscribe = new Subject();
    private readonly userSubscribe = new Subject();
    private readonly covid19PopupKey = 'covid19-popup';
    private readonly longtermPopupKey = 'longterm-popup';
    translations = Translations;
    //appointments: AppointmentViewModel[] = [];

    @ViewChildren(ModalComponent)
    modals: QueryList<ModalComponent>;

    @ViewChild(FeedbackComponent, { static: true })
    feedbackComponent: FeedbackComponent;
    user: IUser | undefined;
    appointmentDates: AppointmentDatesViewModel[] = [];
    isUserMenuOpen = false;

    constructor(
        private appointmentService: AppointmentService,
        private userService: UserService
    ) {
        this.userService.getUser().pipe(
            takeUntil(this.ngUnsubscribe),
            map((user) => {
                this.user = user;
                this.appointmentService
                    .getDateAppointments()
                    .pipe(
                        takeUntil(this.ngUnsubscribe),
                        map(dateAppointments => {
                            this.appointmentDates = dateAppointments.map(da => new AppointmentDatesViewModel(da));                     
                        })
                    )
                    .subscribe();
                this.appointmentService.getRegisteredMember().pipe(takeUntil(this.ngUnsubscribe),
                    map((member) => {
                        this.user.isRegistered = member != null;
                    })).subscribe();
            })).subscribe();

    }

    ngOnInit() { 
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.complete();
    }

    login(): void {              
        this.userService.login();
    }

    logout(): void {
        this.userService.logout().pipe(map(() => this.user = undefined), take(1)).subscribe();
        this.isUserMenuOpen = false;
    }

    toggleUserMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.isUserMenuOpen = !this.isUserMenuOpen;
    }

    closeUserMenu(): void {
        this.isUserMenuOpen = false;
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        this.isUserMenuOpen = false;
    }

    toggleLongterm(): void {
        if (!this.user.isRegistered) {
            this.appointmentService.registerLongtermMember().subscribe(() => {
                this.user.isRegistered = true;
                const longtermModal = this.modals.find((m) => m.id === 'longterm');
                localStorage.setItem(this.longtermPopupKey, 'true');
                if (longtermModal) {
                    longtermModal.close();
                }
            });;
        } else {
            this.appointmentService.unRegisterLongtermMember().subscribe(() => {
                this.user.isRegistered = false;
            });;
        }
    }


    dateSelected(date: AppointmentDatesViewModel, appointment: AppointmentViewModel): void {
        if (appointment.isLocked || this.user == null) {
            return;
        }

        if (date.selectedAppointment) {

            if (date.selectedAppointment !== appointment.id) {
                this.appointmentService
                    .saveAppointment(appointment.id)
                    .subscribe((a) => {
                        const viewModel = new AppointmentViewModel(a);
                        viewModel.animationState =
                            AppointmentAnimationState.clicked;
                        this.updateAppointment(a.id, viewModel);
                    });
            }
            this.appointmentService
                .deleteAppointment(date.selectedAppointment)
                .subscribe((a) => {
                    const viewModel = new AppointmentViewModel(a);
                    viewModel.animationState =
                        AppointmentAnimationState.initial;
                    this.updateAppointment(a.id, viewModel);
                });
        } else {
            this.appointmentService
                .saveAppointment(appointment.id)
                .subscribe((a) => {
                    const viewModel = new AppointmentViewModel(a);
                    viewModel.animationState =
                        AppointmentAnimationState.clicked;
                    this.updateAppointment(a.id, viewModel);
                });
        }
    }

    openMap(): void {
        const mapModal = this.modals.filter((m) => m.id === 'map')[0];
        mapModal.open();
    }


    openImprint(): void {
        const imprintModal = this.modals.filter((m) => m.id === 'imprint')[0];
        imprintModal.open();
    }

    openFeedback(): void {
        const feedbackModal = this.modals.find((m) => m.id === 'feedback');
        if (feedbackModal) {
            feedbackModal.open();
        }
    }

    onFeedbackClosed(): void {
        const feedbackModal = this.modals.find((m) => m.id === 'feedback');
        if (feedbackModal) {
            feedbackModal.close();
        }
    }

    covid19PopupClosed(): void {
        localStorage.setItem(this.covid19PopupKey, 'true');
    }

    longtermPopupClosed(): void {
        localStorage.setItem(this.longtermPopupKey, 'true');
    }


    private updateAppointment(
        appointmentId: number,
        newAppointment: AppointmentViewModel
    ) {
        // 3. Im appointmentsByDay-Array ersetzen
        const appointmentDate = this.appointmentDates.find((a) => a.appointments.filter(a => a.id === appointmentId)[0]);
        if (appointmentDate) {
            const dayIndex = appointmentDate.appointments.findIndex(a => a.id === appointmentId);
            if (dayIndex !== -1) {
                appointmentDate.appointments[dayIndex] = newAppointment;
            }
        }
    }  

    onAvatarError(event: Event): void {
        const image = event.target as HTMLImageElement;
        image.style.display = 'none';
    }
}
